import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelList } from '@components/channel-list';
import { DEBOUNCE_VARIANT_DARK } from '@components/debounced-input-search/src/debounced-input-search.type';
import { ConfirmModal, SearchItemModal } from '@components/modals';
import { SubmenuIcon } from '@components/submenu-with-icon';
import { SwitchToggle } from '@components/switch-toggle';
import { DATE_FORMAT_LONG_DATE_WITH_TIME } from '@constants/datetime';
import { useSortable } from '@dnd-kit/sortable';
import { ADMISSION_STATUS_HIDDEN, ADMISSION_STATUS_IN_REVIEW, ADMISSION_STATUS_ON_SALE } from '@domain/admission';
import { Channel, EventChannel } from '@domain/channel';
import { QUERY_KEYS } from '@hooks/admissions/constants';
import { ADMISSION_CARD_TYPE } from '@hooks/admissions/type';
import { useAdmissionCardActions } from '@hooks/admissions/use-admission-card-actions';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { useGetSharePaymentLink } from '@hooks/use-get-share-payment-link/use-get-share-payment-link';
import { useQueryClient } from '@tanstack/react-query';
import { formatNumber } from '@utils/format-number/format-number';
import { fromBackend } from '@utils/map-date/from-backend';

import { AdmissionCardInfo } from '../components/admission-card-info';

import { Props } from './admission-card.type';

// eslint-disable-next-line complexity
export const AdmissionCard = ({
  id,
  timezone,
  name,
  description,
  quantity,
  soldItems,
  currency,
  isPublished,
  isPrivate,
  needsUserVerified,
  amount,
  onlinePrice,
  ddpPrice,
  salesEnds,
  status,
  tags,
  color,
  defaultChannel,
  showSubmenuIcon = true,
  onEdit,
  onDelete,
  onDuplicate,
  onClick,
  onChange,
  onUnassign,
  guestCapacity,
  isPreset,
  isPastEvent,
  checked,
  isSelectActive,
  isMultiSelectActive,
  deleteIsUnassign,
  eventId,
  accessValidity,
  isDragActive,
  passHolders,
  isActive,
  onToggleClick,
  onClickViewHolders,
  onClickAddHolder,
  isPassCard = false,
}: Props) => {
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id,
  });
  const isDateVisible = !!salesEnds && status === ADMISSION_STATUS_ON_SALE;
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const locale = i18n.language;

  // PassCard states
  const [isToggleActive, setIsToggleActive] = useState(!!isActive);
  const [isConfirmToggleModalOpen, setIsConfirmToggleModalOpen] = useState(false);
  const isSharePaymentLinkActive = isPublished && !isPrivate && status === ADMISSION_STATUS_ON_SALE;
  const formattedPrice = formatNumber({
    locale,
    currency,
    number: amount,
    minimumFractionDigits: 0,
  });
  const formattedOnlinePrice = formatNumber({
    locale,
    currency,
    number: onlinePrice ?? 0,
    minimumFractionDigits: 0,
  });
  const formatedDdpPrice = formatNumber({
    locale,
    currency,
    number: ddpPrice ?? 0,
    minimumFractionDigits: 0,
  });

  const salesEndDate = fromBackend(salesEnds);
  const formattedSalesEndsDate = salesEndDate.tz(timezone)?.locale(locale).format(DATE_FORMAT_LONG_DATE_WITH_TIME);
  const isOpenForAll = isPublished && !isPrivate && !needsUserVerified;
  const [isOpenSelectPaymentLinkChannelModal, setIsOpenSelectPaymentLinkChannelModal] = useState(false);
  const [selectedPaymentLinkChannel, setSelectedPaymentLinkChannel] = useState<Channel | undefined>();
  const [searchPaymentLinkChannelsValue, setSearchPaymentLinkChannelsValue] = useState('');
  const [isSharedSubMenuClicked, setIsSharedSubMenuClicked] = useState(false);
  const [paymentLinkChannels, setPaymentLinkChannels] = useState<EventChannel[] | undefined>();

  const { isShareLinkclick, setIsShareLinkclick } = useGetSharePaymentLink({
    channel: selectedPaymentLinkChannel ?? defaultChannel,
    id,
    eventId,
    isPass: isPassCard,
    checkIsChannelActive: isPassCard,
  });
  const { data: eventChannels } = useGetChannels({
    eventId,
    enabled: isSharedSubMenuClicked && eventId !== undefined,
  });
  const { data: offersChannels } = useGetActiveChannels({
    offerId: id,
    enabled: isSharedSubMenuClicked,
  });

  const handleEdit = useCallback(() => {
    if (isPassCard && !isToggleActive && !isPreset) return;
    if (onEdit) onEdit(id);
    if (onClick) onClick();
  }, [onEdit, onClick, id, isPassCard, isToggleActive, isPreset]);

  const handleDuplicate = useCallback(() => {
    if (onDuplicate) onDuplicate();
  }, [onDuplicate]);

  const handleDelete = useCallback(() => {
    if (onDelete) onDelete(id);
  }, [onDelete, id]);

  const handleUnassign = useCallback(() => {
    if (onUnassign) onUnassign(id);
  }, [onUnassign, id]);

  // PassCard handlers
  const handleViewHolders = useCallback(() => {
    if (onClickViewHolders) onClickViewHolders();
  }, [onClickViewHolders]);

  const handleAddHolder = useCallback(() => {
    if (onClickAddHolder) onClickAddHolder();
  }, [onClickAddHolder]);

  const toggleClickHandler = (value: boolean, e: React.KeyboardEvent | React.MouseEvent) => {
    e.stopPropagation();
    setIsConfirmToggleModalOpen(true);
  };

  const handleClickConfirmToggle = () => {
    onToggleClick?.({ isToggleActive, passId: id });
    setIsToggleActive((prev) => !prev);
    setIsConfirmToggleModalOpen(false);
  };

  const handlePassShareLink = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.getOfferChannelsById, id],
    });
    setIsShareLinkclick(true);
  }, [queryClient, id, setIsShareLinkclick]);

  const resetSharePaymentLinkStates = () => {
    setSearchPaymentLinkChannelsValue('');
    setSelectedPaymentLinkChannel(undefined);
    setIsSharedSubMenuClicked(false);
    setPaymentLinkChannels(undefined);
    queryClient.invalidateQueries([
      {
        queryKey: [QUERY_KEYS.getOfferChannelsById, id],
      },
      {
        queryKey: [QUERY_KEYS.getOfferChannelsById, eventId],
      },
    ]);
  };

  const handleCloseSelectPaymentLinkChannelModal = () => {
    setIsOpenSelectPaymentLinkChannelModal(false);
  };

  const handleClickSharedLink = useCallback(() => {
    setIsSharedSubMenuClicked(true);
  }, []);

  useEffect(() => {
    if (!isShareLinkclick) {
      if (isOpenSelectPaymentLinkChannelModal) handleCloseSelectPaymentLinkChannelModal();
      else resetSharePaymentLinkStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShareLinkclick]);

  useEffect(() => {
    if (isSharedSubMenuClicked) {
      const enabledEventChannels = eventChannels?.filter((eventChannel) => eventChannel.isEnabled);
      if (offersChannels?.channels.length === 0) setPaymentLinkChannels(enabledEventChannels);
      else {
        const channels = enabledEventChannels?.filter((eventChannel) => {
          return offersChannels?.channels.some((offerChannel) => offerChannel.id === eventChannel.channel.id);
        });
        setPaymentLinkChannels(channels);
      }
      setIsShareLinkclick(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharedSubMenuClicked, eventChannels, offersChannels]);

  const handleShareLink = useCallback(() => {
    setIsShareLinkclick(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsShareLinkclick]);

  const setDefaultPaymentLinkChannel = (channels: EventChannel[]) => {
    const defaultChannelIndex = channels.findIndex((paymentLinkChannel) => {
      const hasDefaultChannel = paymentLinkChannel.channel.id === defaultChannel?.id;
      return hasDefaultChannel;
    });
    if (defaultChannelIndex >= 0) setSelectedPaymentLinkChannel(channels[defaultChannelIndex].channel);
    else setSelectedPaymentLinkChannel(undefined);
  };

  useEffect(() => {
    if (paymentLinkChannels) {
      if (paymentLinkChannels.length === 1) {
        const channel = paymentLinkChannels[0].channel;
        setSelectedPaymentLinkChannel(channel);
      } else if (!selectedPaymentLinkChannel) {
        setDefaultPaymentLinkChannel(paymentLinkChannels);
      }

      setIsOpenSelectPaymentLinkChannelModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentLinkChannels]);

  const handleSearchPaymentLinkChange = (value: string) => {
    setSearchPaymentLinkChannelsValue(value);
  };

  const ACTIONS = useAdmissionCardActions({
    type: isPassCard ? ADMISSION_CARD_TYPE.pass : ADMISSION_CARD_TYPE.offer,
    isPreset: isPreset ?? false,
    isPastEvent: isPastEvent ?? false,
    isSharePaymentLinkActive,
    isOpenForAll: isOpenForAll ?? false,
    deleteIsUnassign: deleteIsUnassign ?? false,
    isToggleActive: isToggleActive ?? false,
    onEdit,
    onDelete,
    onDuplicate,
    onUnassign,
    onClickViewHolders,
    onClickAddHolder,
    handleEdit,
    handleDelete,
    handleDuplicate,
    handleUnassign,
    handleViewHolders,
    handleAddHolder,
    handleShareLink: isPassCard ? handlePassShareLink : handleClickSharedLink,
    t,
  });

  const cardOpacity = status === ADMISSION_STATUS_HIDDEN || status === ADMISSION_STATUS_IN_REVIEW ? 'opacity-50' : '';

  return (
    <>
      <div
        className='bg-surface-primary shadow-md rounded-md py-5 px-4 sm:p-6 flex flex-col gap-4 justify-between cursor-pointer dark:shadow-black-1000/30'
        style={style}
        ref={setNodeRef}
        role='menuitem'
        tabIndex={0}
        onClick={handleEdit}
        onKeyDown={undefined}>
        <div className='flex flex-row w-full min-w-0'>
          <AdmissionCardInfo
            {...{
              id,
              quantity,
              soldItems,
              tags,
              status,
              locale,
              accessValidity,
              isPreset,
              cardOpacity,
              color,
              name,
              description,
              isDateVisible,
              formattedSalesEndsDate,
              guestCapacity,
              checked,
              listeners,
              attributes,
              isSelectActive,
              isMultiSelectActive,
              onChange,
              onlinePrice,
              ddpPrice,
              formattedOnlinePrice,
              formatedDdpPrice,
              isDragActive,
            }}
          />
          <div className='flex flex-col-reverse justify-between sm:flex-row items-center sm:gap-8 shrink-0'>
            <div className={`text-right text-subtitle-m text-primary  ${cardOpacity}`}>{formattedPrice}</div>
            {showSubmenuIcon && <SubmenuIcon options={ACTIONS} dotsClasses='w-5 h-5' />}
          </div>
        </div>
        {isPassCard && passHolders && (
          <>
            <div className='border-t border-extra-soft' />
            <div className='flex flex-row items-center justify-between'>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewHolders();
                }}
                className='flex items-center gap-1'>
                <span className='text-secondary text-body-xs'>{t('passes:activeLabel')}</span>
                <span className='text-primary text-subtitle-s'>
                  {passHolders.active || 0}/{passHolders.inactive || 0}
                </span>
              </button>
              {!isPreset && (
                <SwitchToggle
                  active={isToggleActive}
                  loading={false}
                  onToggleSwitch={(value: boolean, e: React.KeyboardEvent | React.MouseEvent) =>
                    toggleClickHandler(value, e)
                  }
                />
              )}
            </div>
          </>
        )}
      </div>
      {isPassCard && (
        <ConfirmModal
          title={t('passes:confirmSaveEventPassActivateTitle')}
          isOpen={isConfirmToggleModalOpen}
          handleClickConfirm={handleClickConfirmToggle}
          handleClose={() => setIsConfirmToggleModalOpen(false)}
        />
      )}
      <SearchItemModal
        handleClose={handleCloseSelectPaymentLinkChannelModal}
        isOpen={isOpenSelectPaymentLinkChannelModal}
        placeholder={t('offers:admissionTicketSalesChannelsSearchLabel')}
        title={t('offers:selectChannelModalTitle')}
        buttonPrimaryText={t('common:doneButton')}
        buttonSecondaryText={t('common:discard')}
        isButtonPrimaryDisabled={!selectedPaymentLinkChannel}
        handlePrimaryButton={handleShareLink}
        handleSecondaryButton={handleCloseSelectPaymentLinkChannelModal}
        handleSearch={handleSearchPaymentLinkChange}
        showCloseIconButton={true}
        afterLeave={resetSharePaymentLinkStates}
        debounceInputVariant={DEBOUNCE_VARIANT_DARK}>
        <ChannelList
          handleChange={setSelectedPaymentLinkChannel}
          selectedChannel={selectedPaymentLinkChannel}
          channels={paymentLinkChannels}
          channelSearchValue={searchPaymentLinkChannelsValue}
        />
      </SearchItemModal>
    </>
  );
};
